/**
 * Чернетка подання для екранів подань — списку (`/l/$view`) і дошки (`/b/$view`): одна машина
 * стану, щоб правило «що не йде в адресу» (SEC-110) не розходилося між екранами (views.md, розд. 6).
 *
 * - **Звідки.** Незбережені зміни — з `?d=` (FLT-08), той самий розбір, що й у `loader` маршруту.
 * - **Що не йде в адресу.** Фільтр з полем `personal` / `sensitive` чи задовгий код тримається в
 *   стані екрана (`held`), поки адреса — та сама; людина бачить тост `filterNotInLink`.
 * - **Зіпсоване посилання** — чисте подання і пояснення `viewLinkStale`, без тихої підміни.
 */
import type { FilterNode, MetaEntity, MetaView } from '@clientline/contracts';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { filters as tf } from '../i18n/uk/filters.ts';
import { list as tl } from '../i18n/uk/list.ts';
import { toast } from '../ui/notify/store.ts';
import { filterDraft } from './filters/tree-edit.ts';
import { addressable, decodeDraft, encodeDraft, type ViewDraft } from './view-draft.ts';

export const NO_DRAFT: ViewDraft = {};

/** Екран подання: список або дошка; дошка зберігає в адресі обрану воронку. */
export type ViewDraftScreen = { route: '/l/$view' } | { route: '/b/$view'; pipeline?: string };

/** Фільтр, що не може йти в адресу: тримається, поки адреса — та сама. */
interface HeldFilter {
  d: string | undefined;
  filter: FilterNode | null;
}

export interface ViewDraftState {
  /** Чернетка: з адреси, а фільтр, що не йде в адресу, — зі стану екрана. */
  draft: ViewDraft;
  /** Фільтр чернетки тримається в стані екрана (в адресі його немає). */
  held: boolean;
  /** Відкрити подання; те саме — заміною запису історії, інше — новим. */
  open: (key: string, next?: ViewDraft) => void;
}

export function useViewDraft(
  view: MetaView,
  entity: MetaEntity,
  d: string | undefined,
  screen: ViewDraftScreen,
): ViewDraftState {
  const navigate = useNavigate();
  const decoded = useMemo(() => decodeDraft(d), [d]);
  const [held, setHeld] = useState<HeldFilter | null>(null);
  const draft = useMemo<ViewDraft>(() => {
    const fromUrl = decoded.ok ? decoded.draft : NO_DRAFT;
    return held && held.d === d ? { ...fromUrl, filter: held.filter } : fromUrl;
  }, [decoded, held, d]);
  const { route } = screen;
  const pipeline = screen.route === '/b/$view' ? screen.pipeline : undefined;
  const open = useCallback(
    (key: string, next: ViewDraft = NO_DRAFT) => {
      const keep = key === view.key && next.filter !== undefined && !addressable(next, entity);
      const { filter, ...rest } = next;
      const encoded = encodeDraft(keep ? rest : next);
      setHeld(keep ? { d: encoded, filter: filter ?? null } : null);
      if (keep) toast({ text: tf.filterNotInLink, key: 'filter-not-in-link' });
      const replace = key === view.key;
      const withD = encoded ? { d: encoded } : {};
      if (route === '/b/$view') {
        void navigate({
          to: '/b/$view',
          params: { view: key },
          search: { ...(pipeline ? { pipeline } : {}), ...withD },
          replace,
        });
      } else {
        void navigate({ to: '/l/$view', params: { view: key }, search: withD, replace });
      }
    },
    [navigate, view.key, entity, route, pipeline],
  );
  // Зіпсоване чи застаріле посилання: чисте подання і пояснення, без тихої підміни.
  useEffect(() => {
    if (decoded.ok) return;
    toast({ text: tl.viewLinkStale, key: `view-link-stale:${view.key}` });
    open(view.key);
  }, [decoded.ok, open, view.key]);
  return { draft, held: held !== null && held.d === d, open };
}

/**
 * Чернетка з новим фільтром: `base` — умова подання (`undefined` — конфігурація ще в дорозі, тоді
 * фільтр іде як є); фільтр, що збігся з поданням, із чернетки зникає (`filterDraft`).
 */
export function withFilterDraft(
  shown: ViewDraft,
  next: FilterNode | null,
  base: { filter: FilterNode | undefined } | undefined,
): ViewDraft {
  const { filter: _old, ...rest } = shown;
  const nextFilter = base ? filterDraft(next, base.filter) : next;
  return nextFilter === undefined ? rest : { ...rest, filter: nextFilter };
}
